import { useState } from 'react'
import Analytics from 'APP/Services/Analytics'
import { resizeImage, getImageDimensions } from 'APP/Lib/ResizeImage.web'
import { MAX_HEIGHT_UPLOAD, MAX_WIDTH_UPLOAD, resizeToFit } from 'APP/Lib/ImagePickerHelpers'
import { FileTypes } from 'APP/Converse/FilePreview/types'
import { WebQueueFileInput } from 'APP/Lib/FileUploadHelpers'

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif']
const MAX_FILE_SIZE_BYTES = 34 * 1024 * 1024

interface WebFileSelection {
  file: File
  fileType: string | null
  fileName?: string
}

interface FileInputHandle {
  click: () => void
  value?: string
}

interface UseWebFilePickerOptions {
  submitCallback: (selection: WebFileSelection) => void
  fileInputRef?: { current: FileInputHandle | null } | null
  resize?: boolean
  maxWidth?: number
  maxHeight?: number
  showMultipleFile?: boolean
}

interface FileChangeEvent {
  target?: {
    files?: ArrayLike<File> | null
    value?: string
  } | null
}

type ResizeFileType = 'image/jpeg' | 'image/gif' | 'image/png' | 'image/jpg'

const isPdf = (candidate?: File | null) => {
  const type = candidate?.type?.toLowerCase() || ''
  const name = candidate?.name?.toLowerCase() || ''
  return type.includes('pdf') || name.endsWith('.pdf')
}

const isImage = (candidate?: File | null) => {
  const type = candidate?.type?.toLowerCase() || ''
  const name = candidate?.name?.toLowerCase() || ''
  return type.startsWith('image/') || IMAGE_EXTENSIONS.some((ext) => name.endsWith(`.${ext}`))
}

const isFileSupported = (candidate?: File | null) => isPdf(candidate) || isImage(candidate)

const resolveFileType = (file?: File | null) => {
  if (!file) return null
  if (file.type) return file.type
  if (isPdf(file)) return 'application/pdf'
  if (isImage(file)) return 'image/jpeg'
  return ''
}

const toQueueFile = (file: File, type: FileTypes): WebQueueFileInput => ({
  file: file,
  uri: '',
  name: file.name,
  size: file.size,
  type,
  fileType: resolveFileType(file) ?? '',
})

const formatFileSize = (file?: File | null) => {
  const sizeInBytes = file?.size ?? 0
  return `${(sizeInBytes / 1048576).toFixed(2)}MB`
}

const trackFileUploadError = (reason: string, file?: File | null) => {
  Analytics.trackStructuredEvent(
    'Chat Action',
    'File upload error',
    reason,
    formatFileSize(file),
    0
  )
}

export const useFilePicker = ({
  submitCallback,
  fileInputRef,
  resize = false,
  maxWidth = MAX_WIDTH_UPLOAD,
  maxHeight = MAX_HEIGHT_UPLOAD,
  showMultipleFile = false, // New param for multi-file mode
}: UseWebFilePickerOptions) => {
  const [file, setFile] = useState<File | null>()
  const [isLoading, setLoading] = useState(false)
  const [isTooLarge, setIsTooLarge] = useState(false)
  const [fileSelected, setFileSelected] = useState<WebQueueFileInput | null>(null)
  const hasFile = !!file

  const onFileChange = async (e: FileChangeEvent) => {
    const newFile = e.target?.files?.[0]

    // Reset the input so the same file can be selected again
    if (e.target) {
      e.target.value = ''
    }

    if (!newFile) return

    if (!isFileSupported(newFile)) {
      trackFileUploadError('unsupported_file_type', newFile)
      setFile(undefined)
      setFileSelected(null)
      setLoading(false)
      return
    }

    // Check file size for multi-file mode
    if (showMultipleFile && (newFile.size as number) > MAX_FILE_SIZE_BYTES) {
      trackFileUploadError('file_too_large', newFile)
      setIsTooLarge(true)
      setFileSelected(null)
      setLoading(false)
      return
    }

    setIsTooLarge(false)

    if (!(resize && isImage(newFile))) {
      setFile(newFile)

      // For multi-file mode, also set fileSelected with metadata
      if (showMultipleFile) {
        const fileType = isPdf(newFile) ? FileTypes.PDF : FileTypes.IMAGE
        setFileSelected(toQueueFile(newFile, fileType))
      }

      setLoading(false)
      return
    }

    setLoading(true)
    try {
      const { width, height, uri } = (await getImageDimensions(newFile as File)) as {
        width: number
        height: number
        uri: string
      }

      if (width * height <= maxWidth * maxHeight) {
        setFile(newFile)

        if (showMultipleFile) {
          setFileSelected(toQueueFile(newFile, FileTypes.IMAGE))
        }

        return
      }

      const { targetWidth, targetHeight } = resizeToFit({
        maxWidth,
        maxHeight,
        width,
        height,
      })

      // @ts-expect-error: ResizeImage.web types compress as required, but this call omits it and uses the canvas default
      const resizedAsset = (await resizeImage({
        uri,
        width: targetWidth,
        height: targetHeight,
        fileName: newFile.name ?? '',
        fileType: (resolveFileType(newFile) || 'image/jpeg') as ResizeFileType,
      })) as { file?: File | null } | null

      const processedFile = resizedAsset?.file || newFile
      setFile(processedFile)

      if (showMultipleFile) {
        setFileSelected(toQueueFile(processedFile, FileTypes.IMAGE))
      }

      URL.revokeObjectURL(uri)
    } catch (error) {
      trackFileUploadError(`processing_error: ${error}`, newFile)
      setFile(newFile)

      if (showMultipleFile) {
        setFileSelected(toQueueFile(newFile, FileTypes.IMAGE))
      }
    } finally {
      setLoading(false)
    }
  }

  const openFilePicker = () => {
    fileInputRef?.current?.click()
  }

  const trackFilePicked = (pickedFile?: File | null) => {
    const label = pickedFile?.name || ''
    const property = formatFileSize(pickedFile)

    Analytics.trackStructuredEvent('Chat Action', 'File upload', label, property, 0)
  }

  const onSubmit = (fileObject?: File | null) => {
    if (!fileObject || !isFileSupported(fileObject)) {
      return
    }

    trackFilePicked(fileObject)
    submitCallback({
      file: fileObject,
      fileType: resolveFileType(fileObject),
      fileName: fileObject.name,
    })
  }

  const onSubmitPress = () => {
    if (hasFile) {
      onSubmit(file)
    }
  }

  const onFileChangeSubmit = (e: FileChangeEvent) => {
    const newFile = e.target?.files?.[0]

    if (!newFile) {
      return
    }

    if (!isFileSupported(newFile)) {
      trackFileUploadError('unsupported_file_type', newFile)
      setFile(undefined)
      return
    }

    if ((newFile.size as number) > MAX_FILE_SIZE_BYTES) {
      trackFileUploadError('file_too_large', newFile)
      setFile(undefined)
      setLoading(false)
      return
    }

    trackFilePicked(newFile)
    submitCallback({
      file: newFile,
      fileType: resolveFileType(newFile),
      fileName: newFile.name,
    })
    setFile(newFile)
    setLoading(false)
  }

  const resetFile = () => {
    setFile(null)
    setFileSelected(null)
    setLoading(false)
    setIsTooLarge(false)
    if (fileInputRef?.current) {
      fileInputRef.current.value = ''
    }
  }

  return {
    openFilePicker,
    onFileChange,
    onFileChangeSubmit,
    isLoading,
    onSubmitPress,
    file,
    hasFile,
    isFileSupported,
    resetFile,
    // Multi-file mode properties
    fileSelected,
    isTooLarge,
    setFileSelected,
  }
}
